// ============================================================
// calendarRangeCache.ts
// The calendar's client-side range cache. Two kinds of slot:
//   • Casevo — the firm data for a range + scope: entries (tasks, deadlines,
//     appointments) + day-notes + reminders, fetched TOGETHER (in parallel)
//     and stored as ONE bundle so a range's items are always revealed
//     together, never one source at a time.
//   • Outlook — the user's own Outlook events for a range (scope-independent).
//
// Lookups accept any cached slot that COVERS the asked-for range (the grids
// filter to their own days), so Month → Week → Day inside a loaded month is
// instant. Identical loads in flight are shared (a prefetch and the real
// navigation make one request). A slot is "fresh" for FRESH_MS; an older or
// stale-marked slot is still shown instantly but quietly re-fetched.
//
// ★ Owner-scoped: every slot belongs to `${firmId}:${userId}`; switching owner
// (sign-out → someone else signs in, same tab) drops everything first, so one
// user's calendar can never be shown to another. Bounded (oldest evicted).
// Out-of-order safe: a response older than the slot already stored is dropped.
// ============================================================
import { CalendarEntry, DayNote, Reminder } from '../../types/calendar.types'
import type { OutlookEvent } from '../../api/casevoApi'

export interface CalendarRange {
  from: string // ISO, inclusive
  to: string // ISO, inclusive
}
export type CalendarScope = 'mine' | 'firm'

export interface CasevoBundle {
  entries: CalendarEntry[]
  notes: DayNote[]
  reminders: Reminder[]
}
export interface OutlookBundle {
  connected: boolean
  needsReconnect: boolean
  events: OutlookEvent[]
}

export interface CacheSlot<T> {
  key: string
  range: CalendarRange
  fromMs: number
  toMs: number
  data: T
  fetchedAt: number
  stale: boolean
  seq: number
}

export interface CalendarRangeFetchers {
  fetchCasevo: (range: CalendarRange, scope: CalendarScope) => Promise<CasevoBundle>
  fetchOutlook: (range: CalendarRange, opts: { fresh: boolean }) => Promise<OutlookBundle>
}

export const FRESH_MS = 30_000
export const MAX_SLOTS = 40

export const casevoKeyOf = (range: CalendarRange, scope: CalendarScope): string => `c|${scope}|${range.from}|${range.to}`
export const outlookKeyOf = (range: CalendarRange): string => `o|${range.from}|${range.to}`

export interface CalendarRangeCache {
  setOwner: (owner: string | null) => void
  findCasevo: (range: CalendarRange, scope: CalendarScope) => CacheSlot<CasevoBundle> | undefined
  findOutlook: (range: CalendarRange) => CacheSlot<OutlookBundle> | undefined
  isFresh: (slot: CacheSlot<unknown>) => boolean
  // `force` skips any identical load already in flight (it may predate a write).
  loadCasevo: (range: CalendarRange, scope: CalendarScope, opts?: { force?: boolean }) => Promise<CacheSlot<CasevoBundle>>
  // `fresh` also asks the server to skip its own 60 s cache.
  loadOutlook: (range: CalendarRange, opts?: { force?: boolean; fresh?: boolean }) => Promise<OutlookBundle>
  patchCasevo: (key: string, data: CasevoBundle) => void
  // Marks every Casevo slot stale (after a write / live change).
  markAllStale: () => void
  clear: () => void
  size: () => number
}

export const createCalendarRangeCache = (
  fetchers: CalendarRangeFetchers,
  opts?: { now?: () => number; freshMs?: number; maxSlots?: number },
): CalendarRangeCache => {
  const now = opts?.now ?? Date.now
  const freshMs = opts?.freshMs ?? FRESH_MS
  const maxSlots = Math.max(2, opts?.maxSlots ?? MAX_SLOTS)
  let owner: string | null = null
  // Bumped by clear / owner change so responses for the old owner are dropped.
  let epoch = 0
  let seqCounter = 0
  const slots = new Map<string, CacheSlot<CasevoBundle | OutlookBundle>>()
  const inFlight = new Map<string, Promise<unknown>>()

  const put = <T extends CasevoBundle | OutlookBundle>(key: string, range: CalendarRange, data: T, seq: number): CacheSlot<T> => {
    const prev = slots.get(key)
    // An older response finishing after a newer one never overwrites it.
    if (prev && prev.seq > seq) return prev as CacheSlot<T>
    const slot: CacheSlot<T> = {
      key, range, fromMs: Date.parse(range.from), toMs: Date.parse(range.to), data, fetchedAt: now(), stale: false, seq,
    }
    slots.delete(key) // re-insert → newest
    slots.set(key, slot)
    while (slots.size > maxSlots) {
      const oldest = slots.keys().next().value
      if (oldest === undefined) break
      slots.delete(oldest)
    }
    return slot
  }

  // Exact key first; else the freshest slot with the same prefix that covers
  // the range.
  const find = <T>(prefix: string, exactKey: string, range: CalendarRange): CacheSlot<T> | undefined => {
    const exact = slots.get(exactKey)
    if (exact) return exact as unknown as CacheSlot<T>
    const fromMs = Date.parse(range.from)
    const toMs = Date.parse(range.to)
    let best: CacheSlot<unknown> | undefined
    slots.forEach((s) => {
      if (!s.key.startsWith(prefix)) return
      if (fromMs < s.fromMs || toMs > s.toMs) return
      if (!best || s.fetchedAt > best.fetchedAt) best = s
    })
    return best as CacheSlot<T> | undefined
  }

  const run = <T>(key: string, force: boolean, start: (seq: number, ep: number) => Promise<T>): Promise<T> => {
    if (!force) {
      const pending = inFlight.get(key)
      if (pending) return pending as Promise<T>
    }
    const seq = ++seqCounter
    const p = start(seq, epoch).finally(() => {
      if (inFlight.get(key) === p) inFlight.delete(key)
    })
    inFlight.set(key, p)
    return p
  }

  return {
    setOwner: (next) => {
      if (next === owner) return
      owner = next
      slots.clear()
      inFlight.clear()
      epoch++
    },
    findCasevo: (range, scope) => find<CasevoBundle>(`c|${scope}|`, casevoKeyOf(range, scope), range),
    findOutlook: (range) => find<OutlookBundle>('o|', outlookKeyOf(range), range),
    isFresh: (slot) => !slot.stale && now() - slot.fetchedAt < freshMs,
    loadCasevo: (range, scope, o) => {
      const key = casevoKeyOf(range, scope)
      return run(key, !!o?.force, async (seq, ep) => {
        const data = await fetchers.fetchCasevo(range, scope)
        if (ep !== epoch) {
          // Owner changed / cleared mid-flight — hand back, never store.
          return { key, range, fromMs: Date.parse(range.from), toMs: Date.parse(range.to), data, fetchedAt: now(), stale: false, seq }
        }
        return put(key, range, data, seq)
      })
    },
    loadOutlook: (range, o) => {
      const key = outlookKeyOf(range)
      return run(key, !!o?.force || !!o?.fresh, async (seq, ep) => {
        const data = await fetchers.fetchOutlook(range, { fresh: !!o?.fresh })
        // Only a connected answer is worth keeping.
        if (ep === epoch && data.connected) put(key, range, data, seq)
        return data
      })
    },
    patchCasevo: (key, data) => {
      const s = slots.get(key)
      if (s) slots.set(key, { ...s, data })
    },
    // Casevo slots only: a Casevo write never changes Outlook (that freshens
    // by age, and on focus / poll).
    markAllStale: () => {
      slots.forEach((s, k) => { if (k.startsWith('c|')) slots.set(k, { ...s, stale: true }) })
    },
    clear: () => {
      slots.clear()
      inFlight.clear()
      epoch++
    },
    size: () => slots.size,
  }
}
